import React, { useState, useEffect } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import CheckBox from 'expo-checkbox';

import { adminSetCustomerPassword, sendCustomerResetLink } from '../../../api/Customer';
import { getBrandsData } from '../../../api/Price';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';

import { customerModalstyles } from './styles/CustomerModalStyle';

interface ResetPasswordModalProps {
  isModalVisible: boolean;
  customer: any;
  closeModal: () => void;
}

const ResetPasswordModal = ({ isModalVisible, customer, closeModal }: ResetPasswordModalProps) => {
  const { showAlert } = useAlertModal() as any;
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'set' | 'link'>('set');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validMessage, setValidMessage] = useState('');

  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifySms, setNotifySms] = useState(false);

  const [brands, setBrands] = useState<any[]>([]);
  const [selectedStoreUrl, setSelectedStoreUrl] = useState('');

  useEffect(() => {
    if (!isModalVisible) return;

    setMode('set');
    setPassword('');
    setConfirmPassword('');
    setValidMessage('');
    setNotifyEmail(false);
    setNotifySms(false);
    setSelectedStoreUrl('');

    getBrandsData((jsonRes, status) => {
      if (status === 200 && Array.isArray(jsonRes)) {
        // Only brands with a reachable store URL can receive a reset link.
        const withUrl = jsonRes.filter((b) => b?.storedetail?.store_url);
        setBrands(withUrl);

        // Prefill the dropdown with the customer's home-location brand.
        const homeBrandId = customer?.home_location_tbl?.brand_id;
        const match = withUrl.find((b) => b.id === homeBrandId);
        const def = match || withUrl[0];
        if (def) setSelectedStoreUrl(def.storedetail.store_url);
      } else {
        setBrands([]);
      }
    });
  }, [isModalVisible]);

  const handleSetPassword = () => {
    if (!password.trim() || !confirmPassword.trim()) {
      setValidMessage(msgStr('emptyField'));
      return;
    }
    if (password !== confirmPassword) {
      setValidMessage(msgStr('noMatchPass'));
      return;
    }
    setValidMessage('');
    setIsLoading(true);
    adminSetCustomerPassword(
      { customer_id: customer.id, password, notify: { email: notifyEmail, sms: notifySms } },
      (jsonRes, status) => {
        setIsLoading(false);
        if (status === 200) {
          let extra = '';
          const n = jsonRes?.notify;
          if (n) {
            const parts = [];
            if (n.email) parts.push(`Email: ${n.email}`);
            if (n.sms) parts.push(`SMS: ${n.sms}`);
            if (parts.length) extra = ' (' + parts.join(', ') + ')';
          }
          showAlert('success', (jsonRes?.message || msgStr('passUpdatedSuccessfully')) + extra);
          closeModal();
        } else if (jsonRes && jsonRes.error) {
          showAlert('error', jsonRes.error);
        } else {
          showAlert('error', msgStr('unknownError'));
        }
      }
    );
  };

  const handleSendLink = () => {
    if (!customer?.email) {
      showAlert('error', 'This customer has no email address.');
      return;
    }
    if (!selectedStoreUrl) {
      showAlert('error', 'Please select a store.');
      return;
    }
    const storeUrl = 'https://' + selectedStoreUrl;
    setIsLoading(true);
    sendCustomerResetLink(
      { email: customer.email, clientHost: storeUrl, resetLinkHost: storeUrl },
      (jsonRes, status) => {
        setIsLoading(false);
        if (status === 200) {
          showAlert('success', jsonRes?.message || msgStr('resetPasslinkSent'));
          closeModal();
        } else if (jsonRes && jsonRes.error) {
          showAlert('error', jsonRes.error);
        } else {
          showAlert('error', msgStr('unknownError'));
        }
      }
    );
  };

  if (!isModalVisible) return null;

  return (
    <View style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <BasicModalContainer>
        <ModalHeader label={'Reset Password'} closeModal={closeModal} />
        <ModalBody>
          <View style={{ flexDirection: 'row', marginBottom: 15 }}>
            <Pressable
              style={{ flexDirection: 'row', alignItems: 'center', marginRight: 20 }}
              onPress={() => setMode('set')}
            >
              <CheckBox value={mode === 'set'} onValueChange={() => setMode('set')} style={{ marginRight: 8 }} />
              <Text>{'Set new password'}</Text>
            </Pressable>
            <Pressable
              style={{ flexDirection: 'row', alignItems: 'center' }}
              onPress={() => setMode('link')}
            >
              <CheckBox value={mode === 'link'} onValueChange={() => setMode('link')} style={{ marginRight: 8 }} />
              <Text>{'Send reset link'}</Text>
            </Pressable>
          </View>

          {mode === 'set' ? (
            <View style={{ width: 400 }}>
              <Text style={styles.label}>New Password</Text>
              <TextInput
                style={styles.input}
                secureTextEntry
                value={password}
                onChangeText={setPassword}
                placeholder="New Password"
                placeholderTextColor="#ccc"
              />
              <Text style={styles.label}>Confirm Password</Text>
              <TextInput
                style={styles.input}
                secureTextEntry
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder="Confirm Password"
                placeholderTextColor="#ccc"
              />
              {validMessage.trim() !== '' && <Text style={styles.message}>{validMessage}</Text>}
              <Pressable
                style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}
                onPress={() => setNotifyEmail(!notifyEmail)}
              >
                <CheckBox value={notifyEmail} style={{ marginRight: 10 }} />
                <Text>{'Notify customer by Email'}</Text>
              </Pressable>
              <Pressable
                style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}
                onPress={() => setNotifySms(!notifySms)}
              >
                <CheckBox value={notifySms} style={{ marginRight: 10 }} />
                <Text>{'Notify customer by SMS'}</Text>
              </Pressable>
            </View>
          ) : (
            <View style={{ width: 400 }}>
              <Text style={styles.label}>Store</Text>
              {brands.length > 0 ? (
                <Picker
                  style={styles.select}
                  selectedValue={selectedStoreUrl}
                  onValueChange={setSelectedStoreUrl}
                >
                  {brands.map((b) => (
                    <Picker.Item key={b.id} label={b.brand} value={b.storedetail.store_url} />
                  ))}
                </Picker>
              ) : (
                <Text style={{ marginTop: 8 }}>{'No stores available.'}</Text>
              )}
              <Text style={{ marginTop: 10, color: '#555' }}>
                {`A reset link will be emailed to ${customer?.email || '(no email on file)'}.`}
              </Text>
            </View>
          )}
        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={mode === 'set' ? handleSetPassword : handleSendLink}>
            <Text style={styles.addButton}>{mode === 'set' ? 'Update Password' : 'Send Link'}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
    </View>
  );
};

const styles = customerModalstyles;

export default ResetPasswordModal;
